import React, { Fragment } from "react";
import { Link } from "react-router-dom";

import { apiGet, V2 } from "../../Api";
import { formatDateLocalDayOfWeek, formatDateTimeRelative } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import HasRight from "../HasRight";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../button.css";
import "../../dashboard.css";
import "../../table.css";

interface IDashboardState {
    currentDate?: Date;
    deviceStatus?: any;
    healthFairCount?: number | string | React.ReactChild;
    healthFairSingleCount?: number | string | React.ReactChild;
    surgeryCount?: number | string | React.ReactChild;
    totalSurgeryCount?: number | string | React.ReactChild;
    totalSurgeryBreakdown?: { [n: string]: number } | string;
    deviceInfo?: any[];
}

class DashboardView extends React.Component<IAuthProps, IDashboardState> {
    private refreshTimer?: number;

    constructor(props: Readonly<IAuthProps>) {
        super(props);

        this.state = {
            currentDate: new Date(),
            // deviceStatus: { syncTimestamp: "2018-11-26T17:09:00-05:00", voltage: 3.5, percent: Math.min(1, Math.max(0, ((3.5 - 3.4) / 0.7))) },
            healthFairCount: <span className="spinner lds-dual-ring-small" />,
            healthFairSingleCount: <span className="spinner lds-dual-ring-small" />,
            surgeryCount: <span className="spinner lds-dual-ring-small" />,
            totalSurgeryCount: <span className="spinner lds-dual-ring-small" />,
        };

        this.refreshData = this.refreshData.bind(this);
    }

    public async componentDidMount() {
        this.refreshData();

        this.refreshTimer = window.setInterval(this.refreshData, 5 * 60 * 1000);
    }

    public componentWillUnmount() {
        clearInterval(this.refreshTimer);
    }

    public render() {
        return (
            <div className="viewer-root">
                <div className="dash-container">
                    <div className="info-block dash-block">
                        <header>
                            <h3>Welcome</h3>
                        </header>
                        <span>
                            Today is {formatDateLocalDayOfWeek(this.state.currentDate || new Date())}.<br />
                            {/*You have 0 notifications.*/}
                        </span>
                    </div>
                    <HasRight right="fair:read">
                        <div className="info-block dash-block">
                            <header>
                                <h3>Health Fairs</h3>
                            </header>
                            <span>
                                There are {this.state.healthFairCount === 0 ? "no" : this.state.healthFairCount} health fair(s) today.<br />
                                You have {this.state.healthFairSingleCount === 0 ? "none" : this.state.healthFairSingleCount} of them.
                            </span>
                            <br />
                            <div className="button-panel-card">
                                <Link to="/fairs/schedule" className="button btn-light push-right">Full Schedule</Link>
                                <Link to="/fairs" className="button">View Today</Link>
                            </div>
                        </div>
                    </HasRight>
                    {
                        !this.props.authContext.serverType
                            ? <HasRight right="surgery:read">
                                <div className="info-block dash-block">
                                    <header>
                                        <h3>Surgery Center</h3>
                                    </header>
                                    <span>
                                        There are {this.state.surgeryCount === 0 ? "no" : this.state.surgeryCount} case(s) today.<br />
                                    </span>
                                    <br />
                                    <div className="button-panel-card">
                                        <Link to="/surgery/calendar" className="button btn-light push-right">View Calendar</Link>
                                        <Link to="/surgery/calendar/today" className="button">View Today</Link>
                                    </div>
                                </div>
                            </HasRight>
                            : null
                    }
                    {
                        !this.props.authContext.serverType
                            ? <HasRight right="dash:stats">
                                <div className="info-block dash-block">
                                    <header>
                                        <h3>Stats</h3>
                                    </header>
                                    <div>
                                        We've completed a total of <b>{this.state.totalSurgeryCount}</b> surgeries.<br />
                                        {
                                            typeof this.state.totalSurgeryBreakdown === "object"
                                                ? <Fragment>
                                                    Including:<br />
                                                    <br />
                                                    <table className="table-bare">
                                                        <tr><td className="col-bold col-right col-smallest">{(this.state.totalSurgeryBreakdown["58570"] || 0).toLocaleString()}</td><td>Hysterectomies</td></tr>
                                                        <tr><td className="col-bold col-right col-smallest">{(this.state.totalSurgeryBreakdown["57288"] || 0).toLocaleString()}</td><td>TVTs</td></tr>
                                                        <tr><td className="col-bold col-right col-smallest">{(this.state.totalSurgeryBreakdown["58563"] || 0).toLocaleString()}</td><td>Ablations</td></tr>
                                                        <tr><td className="col-bold col-right col-smallest">{(this.state.totalSurgeryBreakdown["58660"] || 0).toLocaleString()}</td><td>Op Laps</td></tr>
                                                    </table>
                                                </Fragment>
                                                : null
                                        }
                                    </div>
                                </div>
                            </HasRight>
                            : null
                    }
                    {
                        this.state.deviceStatus ? (
                            <div className="info-block dash-block">
                                <header>
                                    <h3>Device Status</h3>
                                </header>
                                <dl className="info-list">
                                    <dt>Last sync</dt>
                                    <dd>{formatDateTimeRelative(this.state.deviceStatus.syncTimestamp)}</dd>
                                    <dt>Battery</dt>
                                    <dd>
                                        <i className={`icon-style-centered icon-style-larger icon-battery-${Math.round(this.state.deviceStatus.percent * 4)} ${this.state.deviceStatus.percent < 0.125 ? "icon-style-blink" : ""}`} /> {this.state.deviceStatus.percent < 0.15 ? <i className="icon-style-centered icon-style-larger icon-style-red icon-exclamation-circle" title="Battery very low" /> : ""} {Math.round(this.state.deviceStatus.percent * 100)} % {this.state.deviceStatus.isCharging ? "Plugged in" : ""}
                                    </dd>
                                </dl>
                            </div>
                        ) : this.state.deviceInfo && this.state.deviceInfo.length !== 0
                                ? (
                                    <div className="info-block dash-block">
                                        <header>
                                            <h3>Device Status</h3>
                                        </header>
                                        {
                                            this.state.deviceInfo.length === 1
                                                ? <span>
                                                    Your device was last seen <b>{formatDateTimeRelative(this.state.deviceInfo[0].lastSeen)}</b>
                                                </span>
                                                : <span>
                                                    Your devices were last seen:<br />
                                                    <br />
                                                    {this.state.deviceInfo.map((a: any) => <div>{a.name || "NO"} - <b>{formatDateTimeRelative(a.lastSeen)}</b></div>)}
                                                </span>
                                        }
                                    </div>
                                ) : <div className="dash-block" />
                    }
                    {/* Spacers to keep last item from taking up a whole row */}
                    <div className="dash-block" />
                    <div className="dash-block" />
                </div>
            </div>
        );
    }

    private async refreshData() {
        const newData: Partial<IDashboardState> = {};

        const start = new Date();
        start.setHours(0, 0, 0);

        const end = new Date(start);
        end.setDate(start.getDate() + 1);

        if (this.props.authContext.hasRight("fair:read")) {
            // Health fair count
            try {
                const [allCount, singleCount] = await Promise.all([
                    apiGet<any>(`fairs/count?date_min=${Math.floor(start.getTime() / 1000)}&date_max=${Math.floor(end.getTime() / 1000)}`),
                    apiGet<any>(`fairs/count?date_min=${Math.floor(start.getTime() / 1000)}&date_max=${Math.floor(end.getTime() / 1000)}&person=caller`)
                ]);

                newData.healthFairCount = allCount.count;
                newData.healthFairSingleCount = singleCount.count;
            }
            catch (e) {
                newData.healthFairCount = "(error)";
                newData.healthFairSingleCount = "(error)";
            }
        }

        newData.currentDate = new Date();

        if (!this.props.authContext.serverType && this.props.authContext.hasRight("surgery:read")) {
            // Surgery count
            try {
                const surgeryCount = await V2.get<any>(`surgery/count?after=${start.toLocalISOString()}&before=${end.toLocalISOString()}&status=complete,active`);

                newData.surgeryCount = surgeryCount;
            }
            catch (e) {
                newData.surgeryCount = "(error)";
            }
        }

        if (!this.props.authContext.serverType && this.props.authContext.hasRight("dash:stats")) {
            // Total surgery count
            try {
                const totalSurgeryCount = await V2.get<any>("surgery/count-total");

                newData.totalSurgeryCount = totalSurgeryCount.count.toLocaleString();
                newData.totalSurgeryBreakdown = totalSurgeryCount.types;
            }
            catch (e) {
                newData.totalSurgeryCount = "(error)";
                newData.totalSurgeryBreakdown = "";
            }
        }

        try {
            const json = await apiGet<any>("status");

            newData.deviceStatus = json;
            if (json) {
                newData.deviceStatus.percent = Math.min(1, Math.max(0, ((newData.deviceStatus.voltage - 3.4) / 0.7)));
            }
        }
        catch (e) {
            newData.deviceStatus = undefined;
        }

        try {
            const devInfo = await apiGet<any>("devices/own");

            newData.deviceInfo = devInfo;
        }
        catch (e) {
            newData.deviceInfo = undefined;
        }

        this.setState(newData);
    }
}

export default withTitle("Dashboard", withAuth(DashboardView));
